import React from 'react';
import { motion } from 'framer-motion';
import {
  UserGroupIcon,
  CheckCircleIcon,
  ChartBarIcon,
  BanknotesIcon
} from '@heroicons/react/24/outline';
import ApiLoader from '../../ui/ApiLoader';

const DashboardTab = ({ 
  loading, 
  dashboardData, 
  isDarkMode,
  formatDate 
}) => {
  if (loading) {
    return (
      <ApiLoader label="Loading dashboard data..." className="py-20" />
    );
  }

  return (
    <div className="w-full">
      {/* Lead Statistics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {[
          {
            title: 'Total Leads',
            value: dashboardData?.lead_stats?.total_leads || 0,
            icon: UserGroupIcon,
            color: 'purple',
            change: `${dashboardData?.lead_stats?.new_leads_this_month || 0} new this month`
          },
          {
            title: 'Qualified Leads',
            value: dashboardData?.lead_stats?.qualified_leads || 0,
            icon: CheckCircleIcon,
            color: 'blue',
            change: `${((dashboardData?.lead_stats?.qualified_leads || 0) / (dashboardData?.lead_stats?.total_leads || 1) * 100).toFixed(1)}% of total`
          },
          {
            title: 'Conversion Rate',
            value: `${dashboardData?.lead_stats?.conversion_rate || 0}%`,
            icon: ChartBarIcon,
            color: 'green',
            change: `${dashboardData?.lead_stats?.converted_leads || 0} converted`
          },
          {
            title: 'Pipeline Value',
            value: `$${(dashboardData?.lead_stats?.pipeline_value || 0).toLocaleString()}`,
            icon: BanknotesIcon,
            color: 'emerald',
            change: 'Total opportunity value'
          }
        ].map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">{stat.title}</h3>
              <stat.icon className={`w-6 h-6 text-${stat.color}-500`} />
            </div>
            <div className="text-2xl font-bold mb-1">{stat.value}</div>
            <div className={`text-xs font-medium text-${stat.color}-600`}>{stat.change}</div>
          </motion.div>
        ))}
      </div>

      {/* Customer Statistics */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mt-4 sm:mt-6">
        {[
          {
            title: 'Total Customers',
            value: dashboardData?.total_customers || 0,
            icon: UserGroupIcon,
            color: 'blue',
            change: `+${dashboardData?.growth_metrics?.monthly_growth_rate || 12}% this month`,
            subtext: 'Multi-tenant organizations'
          },
          {
            title: 'New Customers This Month',
            value: dashboardData?.new_customers_this_month || 0,
            icon: BanknotesIcon,
            color: 'green',
            change: `+${((dashboardData?.growth_metrics?.monthly_growth_rate || 12) * 0.8).toFixed(1)}% MoM`,
            subtext: 'New sign-ups'
          },
          {
            title: 'Platform Utilization',
            value: `${((dashboardData?.active_customers || 0) / (dashboardData?.total_customers || 1) * 100).toFixed(1)}%`,
            icon: ChartBarIcon,
            color: 'purple',
            change: '+2.3% improvement',
            subtext: 'Customer engagement'
          },
          {
            title: 'Customer growth rate',
            value: `${dashboardData?.growth_metrics?.monthly_growth_rate || 0}%`,
            icon: ChartBarIcon,
            color: 'purple',
            change: '+2.3% improvement',
            subtext: 'Customer engagement'
          }
        ].map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">{stat.title}</h3>
              <stat.icon className={`w-6 h-6 text-${stat.color}-500`} />
            </div>
            <div className="text-2xl font-bold mb-1">{stat.value}</div>
            <div className={`text-xs font-medium text-${stat.color}-600`}>{stat.change}</div>
          </motion.div>
        ))}
      </div>

      {/* Charts and Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mt-4 sm:mt-6">
        {/* Lead Status Distribution */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Lead Status Distribution</h3>
          <div className="space-y-4">
            {dashboardData?.lead_stats?.status_distribution && Object.entries(dashboardData.lead_stats.status_distribution).map(([status, count]) => {
              const total = dashboardData?.lead_stats?.total_leads || 1;
              const percentage = ((count / total) * 100).toFixed(1);
              const statusColors = {
                'New Lead': 'gray',
                'Contacted': 'yellow',
                'Qualified': 'blue',
                'Proposal Sent': 'indigo',
                'Negotiation': 'orange',
                'Confirmed': 'green',
                'Lost': 'red'
              };
              const color = statusColors[status] || 'gray';
              
              return (
                <div key={status} className="flex flex-col xs:flex-row xs:items-center justify-between gap-2 xs:gap-0">
                  <div className="flex items-center space-x-2 xs:space-x-3">
                    <div className={`w-4 h-4 rounded-full bg-${color}-500`}></div>
                    <span className="font-medium text-gray-900 text-xs xs:text-sm">{status}</span>
                  </div>
                  <div className="flex flex-col xs:flex-row xs:items-center space-y-1 xs:space-y-0 xs:space-x-3 w-full xs:w-auto">
                    <div className="w-full xs:w-24 bg-gray-200 rounded-full h-2">
                      <div 
                        className={`bg-${color}-500 h-2 rounded-full transition-all duration-300`}
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                    <span className="text-xs xs:text-sm font-medium text-gray-600 text-right xs:w-12">
                      {count}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Lead Source Distribution */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Lead Source Distribution</h3>
          <div className="space-y-4">
            {dashboardData?.lead_stats?.source_distribution && Object.entries(dashboardData.lead_stats.source_distribution).map(([source, count]) => {
              const total = dashboardData?.lead_stats?.total_leads || 1;
              const percentage = ((count / total) * 100).toFixed(1);
              
              return (
                <div key={source} className="flex flex-col xs:flex-row xs:items-center justify-between gap-2 xs:gap-0">
                  <span className="font-medium text-gray-900 text-xs xs:text-sm">{source || 'Unknown'}</span>
                  <div className="flex flex-col xs:flex-row xs:items-center space-y-1 xs:space-y-0 xs:space-x-3 w-full xs:w-auto">
                    <div className="w-full xs:w-24 bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-purple-500 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                    <span className="text-xs xs:text-sm font-medium text-gray-600 text-right xs:w-12">
                      {count}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mt-4 sm:mt-6">
        {/* Recent Customers */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Recent Customers</h3>
          <div className="space-y-4">
            {dashboardData?.recent_customers && dashboardData.recent_customers.length > 0 ? (
              dashboardData.recent_customers.map((customer, index) => (
                <div 
                  key={index} 
                  className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 rounded-lg gap-2"
                  style={{ cursor: 'pointer' }}
                >
                  <div className="flex-1 text-left">
                    <h4 className="font-medium text-gray-900 dark:text-white">{customer.company_name}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{customer.email}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {customer.created_date ? formatDate(customer.created_date) : 'N/A'}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500 dark:text-gray-400 text-sm">No recent customers found</p>
              </div>
            )}
          </div>
        </div>

        {/* Recent CRM Leads */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">Recent CRM Leads</h3>
          <div className="space-y-4">
            {dashboardData?.recent_leads && dashboardData.recent_leads.length > 0 ? (
              dashboardData.recent_leads.map((lead, index) => (
                <div 
                  key={index} 
                  className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 rounded-lg gap-2"
                  style={{ cursor: 'pointer' }}
                >
                  <div className="flex-1 text-left">
                    <h4 className="font-medium text-gray-900 dark:text-white">{lead.company_name}</h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{lead.primary_contact_email}</p>
                  </div>
                  <div className="text-right">
                    <span 
                      className="px-2 py-1 text-xs rounded-full font-medium"
                      style={{
                        backgroundColor: lead.status === 'Confirmed' ? (isDarkMode ? 'rgba(16, 185, 129, 0.2)' : '#d1fae5') :
                                        lead.status === 'Qualified' ? (isDarkMode ? 'rgba(59, 130, 246, 0.2)' : '#dbeafe') :
                                        lead.status === 'Contacted' ? (isDarkMode ? 'rgba(251, 191, 36, 0.2)' : '#fef3c7') :
                                        lead.status === 'New Lead' ? (isDarkMode ? 'rgba(156, 163, 175, 0.2)' : '#f3f4f6') :
                                        lead.status === 'Lost' ? (isDarkMode ? 'rgba(239, 68, 68, 0.2)' : '#fee2e2') :
                                        lead.status === 'Proposal Sent' ? (isDarkMode ? 'rgba(99, 102, 241, 0.2)' : '#e0e7ff') :
                                        lead.status === 'Negotiation' ? (isDarkMode ? 'rgba(249, 115, 22, 0.2)' : '#fed7aa') :
                                        (isDarkMode ? 'rgba(156, 163, 175, 0.2)' : '#f3f4f6'),
                        color: lead.status === 'Confirmed' ? (isDarkMode ? '#86efac' : '#065f46') :
                              lead.status === 'Qualified' ? (isDarkMode ? '#93c5fd' : '#1e40af') :
                              lead.status === 'Contacted' ? (isDarkMode ? '#fcd34d' : '#92400e') :
                              lead.status === 'New Lead' ? (isDarkMode ? '#d1d5db' : '#1f2937') :
                              lead.status === 'Lost' ? (isDarkMode ? '#fca5a5' : '#991b1b') :
                              lead.status === 'Proposal Sent' ? (isDarkMode ? '#a5b4fc' : '#3730a3') :
                              lead.status === 'Negotiation' ? (isDarkMode ? '#fdba74' : '#9a3412') :
                              (isDarkMode ? '#d1d5db' : '#1f2937')
                      }}
                    >
                      {lead.status}
                    </span>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {lead.created_at ? formatDate(lead.created_at) : 'N/A'}
                    </p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500 dark:text-gray-400 text-sm">No recent leads found</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardTab;
